import { CommonModule } from '@angular/common';
import { Component, computed, OnDestroy, OnInit, output, signal } from '@angular/core';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { NgxMaskDirective, provideNgxMask } from 'ngx-mask';
import { IBaseItem } from '../../common-items/BaseItem/base-item-form.component';
import {
  iHasCaptionLeft,
  IHasCaptionLeft,
  iHasInputColorControls,
  IHasInputColorControls,
  InteractiveItemComponent
} from '../../common-items/InteractiveItem/interactive-item-form.component';
import {
  DEFAULT_INTERACTIVE_PROPERTIES,
  InteractiveItemProperties
} from '../../common-items/InteractiveItem/interactive-item-properties';

import { getPropsFrom, upsertChildren, xmlElementToString } from '@lfw/roesberg.liveforms.core';
import {
  extractColorProperties,
  getColors,
  getInputStyle,
  getTextColorsPropertiesStyle
} from '../../../../utils/colors';
import { getOptimalFirstColumnWidth } from '../../../../utils/getOptimalWidth';

const DEFAULT_INT_DIGITS = 6;
const DEFAULT_DEC_DIGITS = 2;

interface NumericInputProperties {
  numericInput: number | null;
  text: string;
  style: string;
  inputCaptionLeft: string;
  inputCaptionRight: string;
  numberOfDecimalDigits: number;
  numberOfIntDigits: number;
  captionWidth: number;
}

@Component({
  selector: 'lfe-numeric-input-item-form',
  imports: [CommonModule, FormsModule, ReactiveFormsModule, NgxMaskDirective],
  providers: [provideNgxMask()],
  templateUrl: './numeric-input-item-form.component.html',
  standalone: true,
  styleUrl: './numeric-input-item-form.component.css'
})
export class NumericInputItemFormComponent
  extends iHasInputColorControls(iHasCaptionLeft(InteractiveItemComponent<NumericInputProperties>))
  implements OnDestroy, OnInit, IBaseItem<NumericInputProperties>, IHasCaptionLeft, IHasInputColorControls
{
  readonly updateFirstColumnWidthRequired = output<{ id: string; width: number }>();
  firstColumnWidth = computed(
    () =>
      this.sharedFirstColumnWidths()[this.firstSiblingItemId()] ||
      getOptimalFirstColumnWidth(this.properties.inputCaptionLeft)
  );

  formControl: FormControl<number | null> | undefined;
  properties: InteractiveItemProperties & NumericInputProperties = {
    ...DEFAULT_INTERACTIVE_PROPERTIES,
    numericInput: null,
    text: '',
    style: '',
    inputCaptionLeft: '',
    inputCaptionRight: '',
    numberOfDecimalDigits: DEFAULT_DEC_DIGITS,
    numberOfIntDigits: DEFAULT_INT_DIGITS,
    captionWidth: 0
  };
  initialValue: number | null = 0;

  currentId = computed(() => this.properties.customId || this.properties.id);
  htmlId = computed(() => `numeric_input_${this.formControlId}`);

  currentTextColor = signal('');

  textStyle = computed(() => getTextColorsPropertiesStyle(this.properties.textBgColor, this.currentTextColor()));
  inputStyle = computed(() => getInputStyle(this.currentInputBgColor(), this.currentInputTextColor()));

  // Convert Format string to NgxMask pattern
  mask = computed(() =>
    this.properties.numberOfDecimalDigits > 0 ? `separator.${this.properties.numberOfDecimalDigits}` : 'separator.0'
  );

  numberOutputTransform = (value: string | number | null | undefined): string | number | null | undefined => {
    if (!value || typeof value !== 'string') return value;
    return value
      .replace(new RegExp('\\' + this.settings().numberFormat.thousandsSeparator, 'g'), '')
      .replace(this.settings().numberFormat.decimalSeparator, '.');
  };

  getCaptionLeftStyle(): { [key: string]: string } | null {
    if (!this.properties.inputCaptionLeft) {
      return null;
    }

    return {
      width: `${this.firstColumnWidth()}px`,
      overflow: 'hidden',
      'text-overflow': 'ellipsis',
      'white-space': 'nowrap',
      display: 'inline-block',
      ...this.textStyle()
    };
  }

  getCaptionLeftTitle(): string | null {
    if (!this.properties.inputCaptionLeft) {
      return null;
    }

    return this.properties.inputCaptionLeft;
  }

  override ngOnInit() {
    super.ngOnInit();

    this.formControlId = this.properties.customId || this.properties.id;

    this.initialValue = this.properties.numericInput;

    this.formControl = new FormControl(this.initialValue);
    this.form().addControl(this.formControlId, this.formControl);

    this.updateFirstColumnWidthRequired.emit({
      id: this.firstSiblingItemId(),
      width: this.firstColumnWidth()
    });
  }

  onChange(event: Event): void {
    const target = event.target as HTMLInputElement;
    const value = target.value;

    // Transform the value using the same logic as the table item
    const transformedValue = this.numberOutputTransform(value);
    const numericValue = transformedValue ? Number(transformedValue) : null;

    this.properties.numericInput = numericValue;
    this.properties.resultValue = numericValue !== null ? String(numericValue) : '';
    this.properties.resultTime = new Date().toISOString();
    this.properties.resultUser = this.user().displayName;
  }

  onBlur(event: Event): void {
    const target = event.target as HTMLInputElement;
    const value = target.value;

    if (this.formControl) {
      const transformedValue = this.numberOutputTransform(value);
      const numericValue = transformedValue ? Number(transformedValue) : null;
      this.formControl.setValue(numericValue);
    }
  }

  extractLocalProperties(xml: Element): InteractiveItemProperties & NumericInputProperties {
    const props = getPropsFrom(xml);

    const customId = props['CustomID'];
    const id = props['ID'];
    const text = props['Text'] || '';
    const style = props['Style'] || '';
    const numericInputStr = props['NumericInput'] || '';
    const numericInput = numericInputStr.trim() === '' ? null : Number(numericInputStr);
    const inputCaptionLeft = props['InputCaptionLeft'] || '';
    const inputCaptionRight = props['InputCaptionRight'] || '';
    const numberOfDecimalDigits = Number(props['NumberOfDecimalDigits'] || DEFAULT_DEC_DIGITS);
    const numberOfIntDigits = Number(props['NumberOfIntDigits'] || DEFAULT_INT_DIGITS);
    const captionWidth = Number(props['CaptionWidth'] || 0);
    const activationRule = props['ActivationRule'] || '';
    const isGenericRepeatItem = props['IsGenericRepeatItem'] === 'True';

    const { textBgColor, textColor } = getColors(extractColorProperties(props));

    this.currentTextColor.set(textColor);

    const inputColor = this.initInputColorProperties(props);

    const resultValue = props['ResultValue'] || '';
    const resultUser = props['ResultUser'] || '';
    const resultTime = props['ResultTime'] || '';

    return {
      ...DEFAULT_INTERACTIVE_PROPERTIES,
      id,
      customId,
      activationRule,
      resultValue,
      resultUser,
      resultTime,
      numericInput,
      text,
      textBgColor,
      inputColor,
      style,
      inputCaptionLeft,
      inputCaptionRight,
      numberOfDecimalDigits,
      numberOfIntDigits,
      captionWidth,
      isGenericRepeatItem
    };
  }

  override getXML(): string {
    const element = this.getXmlCloneElement();

    this.handleInputColorStorage(element);

    if (this.properties.numericInput !== this.initialValue) {
      upsertChildren(element, {
        NumericInput: String(this.properties.numericInput)
      });

      this.setResultChildren(element);
    }

    return xmlElementToString(element) || '';
  }

  protected readonly Math = Math;
}
